import Link from 'next/link';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import Merchant from '../../../domain/entities/Merchant';
import MerchantCategory from '../../../domain/entities/MerchantCategory';
import Button, { ButtonSize } from '../../common/buttons/Button';
import Icon from '../../common/icons/Icon';
import ReviewList from '../../common/reviews/ReviewList';
import Heading from '../../common/sections/Heading';
import BaseTemplate, { TemplateProps } from '../common/BaseTemplate';
import DescriptionPanel from '../common/DescriptionPanel';
import MerchantProfile from './MerchantProfile';

import { BreadCrumbsProps } from '../../../utils/breadcrumbs';
import Lightbox from '../../common/images/Lightbox';
import css from './MerchantShowPage.module.scss';
import Review from '../../../domain/entities/Review';

const MAX_REVIEWS = 3;

export interface PageProps extends TemplateProps {
  reviews: Review[];
  merchant: Merchant;
  categories: MerchantCategory[];
  category: MerchantCategory;
}

type Props = PageProps & InjectedIntlProps & BreadCrumbsProps;

const MerchantShowPage = ({
  intl,
  breadcrumbs,
  categories,
  merchant,
  reviews,
  ...data
}: Props) => {
  const canShowReviews =
    merchant.setting.isCustomerReviewActive && reviews && reviews.length > 0;
  const contactPageHref =
    merchant.setting.contactUrl || `/${merchant.publicId}/contact`;

  return (
    <div className={css.MerchantShowPage}>
      <BaseTemplate
        {...data}
        footerProps={{ categories, isMerchantStickyFooterExist: true }}
        merchant={merchant}
        breadcrumbs={breadcrumbs}
      >
        <div className={css.MerchantShowPage__Content}>
          {merchant.description && (
            <div className={css.MerchantShowPage__Section}>
              <DescriptionPanel description={merchant.description} />
            </div>
          )}

          {merchant.gallery.length > 0 && (
            <div className={css.MerchantShowPage__Section}>
              <Heading className={css.MerchantShowPage__Heading}>
                {intl.formatMessage({ id: 'merchants.details.photoGallery' })}
              </Heading>
              <Lightbox
                images={merchant.gallery.map(image => ({
                  thumbUrl: image.thumbnailUrl,
                  mainUrl: image.mainUrl,
                  id: image.id,
                }))}
              />
            </div>
          )}

          <div className={css.MerchantShowPage__Section}>
            <Heading className={css.MerchantShowPage__Heading}>
              {intl.formatMessage(
                { id: 'merchants.details.storeData' },
                { displayName: merchant.displayName },
              )}
            </Heading>
            <MerchantProfile merchant={merchant} />
            <div className={css.MerchantShowPage__LinkWrap}>
              <Button
                className={css.MerchantShowPage__OutlineLink}
                isGhost={true}
                isFull={true}
                href={contactPageHref}
                buttonSize={ButtonSize.Medium}
              >
                {intl.formatMessage({
                  id: 'merchants.details.contactUs',
                })}
              </Button>
            </div>
          </div>

          {canShowReviews && (
            <div className={css.MerchantShowPage__Section}>
              <Heading className={css.MerchantShowPage__Heading}>
                {intl.formatMessage({ id: 'reviews.title' })} (
                {merchant.rating.totalCount})
              </Heading>
              <ReviewList
                merchant={merchant}
                reviews={reviews}
                max={MAX_REVIEWS}
                rate_count_starby={merchant.rating.points}
              />

              {reviews.length > 0 && (
                <Link
                  href={`/${merchant.publicId}/reviews#pageContent`}
                  prefetch={false}
                >
                  <a className={css.MerchantShowPage__ReviewLink}>
                    {intl.formatMessage({
                      id: 'merchants.details.showMoreReviews',
                    })}
                    <Icon
                      className={css.MerchantShowPage__ReviewLinkIcon}
                      name="arrow-right"
                    />
                  </a>
                </Link>
              )}
            </div>
          )}
          <div className={css.MerchantShowPage__BackToTopButtonSection}>
            <Button
              className={css.MerchantShowPage__BackToTopButton}
              isGhost={true}
              isFull={true}
              href={`/${merchant.publicId}`}
              buttonSize={ButtonSize.Medium}
            >
              {intl.formatMessage(
                {
                  id: 'merchants.details.backToMerchantTop',
                },
                { displayName: merchant.displayName },
              )}
            </Button>
          </div>
        </div>
      </BaseTemplate>
    </div>
  );
};

export default injectIntl(MerchantShowPage);
